import { useAuth } from '../contexts/AuthContext';
import { useLAScope } from '../contexts/LAScopeContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { BrandLogo } from './BrandLogo';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Button } from '../components/ui/button';
import { Home, Search, Map, BarChart3, Target, MessageSquare, Settings, LogOut, User, ChevronDown, Menu, X, Radio, FileText, AlertTriangle, CreditCard, Sparkles, MapPin, Store } from 'lucide-react';
import { useState, useEffect } from 'react';
import api from '../lib/api';
import { useEntitlements } from '../contexts/EntitlementsContext';

// Direct (always visible) top-level links
const directItems = [
  { path: '/', label: 'Home', icon: Home },
  { path: '/search', label: 'Search', icon: Search },
  { path: '/chat', label: 'AI Query', icon: MessageSquare },
];

// Grouped dropdowns
const groups = [
  {
    key: 'insights',
    label: 'Insights',
    icon: BarChart3,
    items: [
      { path: '/analytics', label: 'Analytics', icon: BarChart3 },
      { path: '/connectivity', label: 'Connectivity', icon: Radio },
      { path: '/interventions/matrix', label: 'Priority Matrix', icon: AlertTriangle },
      { path: '/map', label: 'Intelligence Map', icon: Map },
    ],
  },
  {
    key: 'plan',
    label: 'Plan',
    icon: Target,
    items: [
      { path: '/interventions', label: 'Intervention Planner', icon: Target },
      { path: '/reports', label: 'Council Reports', icon: FileText },
    ],
  },
];

// Flat list for the mobile drawer (preserves the original order)
const allMobileItems = [
  { path: '/', label: 'Home', icon: Home },
  { path: '/search', label: 'Business Search', icon: Search },
  { path: '/analytics', label: 'Analytics', icon: BarChart3 },
  { path: '/connectivity', label: 'Connectivity', icon: Radio },
  { path: '/interventions', label: 'Intervention Planner', icon: Target },
  { path: '/interventions/matrix', label: 'Priority Matrix', icon: AlertTriangle },
  { path: '/map', label: 'Intelligence Map', icon: Map },
  { path: '/chat', label: 'AI Query', icon: MessageSquare },
  { path: '/reports', label: 'Council Reports', icon: FileText },
];

export default function Navigation() {
  const { user, logout } = useAuth();
  const { laScope, setLAScope, scopes } = useLAScope();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { ent, refresh: refreshEnt } = useEntitlements();
  useEffect(() => {
    const onQuota = () => refreshEnt && refreshEnt();
    window.addEventListener('adoptic:quota', onQuota);
    return () => window.removeEventListener('adoptic:quota', onQuota);
  }, [refreshEnt]);
  useEffect(() => { if (user && refreshEnt) refreshEnt(); }, [location.pathname, user, refreshEnt]);
  const qCap = ent?.daily_queries_cap;
  const qUsed = ent?.daily_queries_used ?? 0;
  const [hasClaims, setHasClaims] = useState(false);
  useEffect(() => {
    if (!user) return;
    api.get('/claims/mine').then(r => setHasClaims((r.data || []).length > 0)).catch(() => {});
  }, [user, location.pathname]);

  const isLAUser = user?.role === 'la_user' && user?.local_authority;
  useEffect(() => {
    if (isLAUser && laScope !== user.local_authority) setLAScope(user.local_authority);
  }, [isLAUser, laScope, user, setLAScope]);
  const currentScope = scopes.find(s => s.slug === laScope) || scopes[0];

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isAdmin = user?.role === 'admin';
  const isActive = (p) => location.pathname === p;
  const isGroupActive = (group) => group.items.some(it => isActive(it.path));

  const linkBase = 'flex items-center gap-1.5 px-2.5 py-1.5 rounded-[4px] font-body text-xs transition-all';
  const linkActive = 'bg-brand-orange/10 text-brand-orange';
  const linkIdle = 'text-ink/60 hover:text-ink hover:bg-ink/5';

  return (
    <nav className="glass-panel sticky top-0 z-50 px-4 lg:px-6 py-3" data-testid="main-navigation">
      <div className="max-w-[1400px] mx-auto flex items-center justify-between gap-3">
        <BrandLogo className="h-4 sm:h-[18px] w-auto brightness-0" testId="nav-brand-link" wrapperClassName="flex items-center gap-2 shrink-0 group" />

        {/* LA scope picker — mirrors the landing page chip selector, one global scope */}
        {isLAUser ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-ink/5 border border-ink/10 font-body text-[10px] uppercase tracking-[0.1em] text-ink/60 whitespace-nowrap" data-testid="nav-la-scope-locked">
            <MapPin className="w-3 h-3 text-brand-orange" strokeWidth={1.5} />
            {currentScope.short}
          </span>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                data-testid="nav-la-scope"
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-ink/5 border border-ink/10 hover:border-ink/30 transition-colors font-body text-[10px] uppercase tracking-[0.1em] text-ink/70 whitespace-nowrap"
              >
                <MapPin className="w-3 h-3 text-brand-orange" strokeWidth={1.5} />
                {currentScope.short}
                <ChevronDown className="w-3 h-3 opacity-60" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="font-body text-sm min-w-[190px]">
              {scopes.map(s => (
                <DropdownMenuItem
                  key={s.slug}
                  data-testid={`nav-la-option-${s.slug}`}
                  className={`cursor-pointer ${laScope === s.slug ? 'text-brand-orange' : ''}`}
                  onClick={() => setLAScope(s.slug, { manual: true })}
                >
                  <MapPin className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
                  {s.label}
                  {ent?.district_licence && (
                    <span className={`ml-auto pl-3 font-body text-[9px] uppercase tracking-wider ${s.slug === ent.district_licence ? 'text-forest font-semibold' : 'text-ink/35'}`} data-testid={`nav-la-licence-${s.slug}`}>
                      {s.slug === ent.district_licence ? 'Licensed' : 'Freemium'}
                    </span>
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Desktop nav */}
        <div className="hidden lg:flex items-center gap-1">
          {directItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              className={`${linkBase} ${isActive(item.path) ? linkActive : linkIdle}`}
            >
              <item.icon className="w-3.5 h-3.5" strokeWidth={1.5} />
              {item.label}
            </Link>
          ))}

          {groups.map(group => {
            const active = isGroupActive(group);
            return (
              <DropdownMenu key={group.key}>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    data-testid={`nav-group-${group.key}`}
                    className={`${linkBase} ${active ? linkActive : linkIdle}`}
                  >
                    <group.icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                    {group.label}
                    <ChevronDown className="w-3 h-3 ml-0.5 opacity-70" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="font-body text-sm min-w-[200px]">
                  {group.items.map(item => (
                    <DropdownMenuItem
                      key={item.path}
                      asChild
                      data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
                      className="cursor-pointer"
                    >
                      <Link to={item.path} className={isActive(item.path) ? 'text-brand-orange' : ''}>
                        <item.icon className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
                        {item.label}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            );
          })}

          {hasClaims && (
            <Link
              to="/my-business"
              data-testid="nav-my-business"
              className={`${linkBase} ${isActive('/my-business') ? linkActive : linkIdle}`}
            >
              <Store className="w-3.5 h-3.5" strokeWidth={1.5} />
              My Business
            </Link>
          )}

          {isAdmin && (
            <Link
              to="/admin"
              data-testid="nav-admin"
              className={`${linkBase} ${isActive('/admin') ? linkActive : linkIdle}`}
            >
              <Settings className="w-3.5 h-3.5" strokeWidth={1.5} />
              Admin
            </Link>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" data-testid="user-menu-trigger" className="font-body text-xs gap-1.5 text-ink/70 hover:text-ink">
                <User className="w-3.5 h-3.5" strokeWidth={1.5} />
                <span className="hidden sm:inline max-w-[140px] truncate">{user?.name || user?.email}</span>
                <ChevronDown className="w-3 h-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="font-body text-sm">
              <div className="px-2 py-1.5">
                <p className="text-xs text-ink/50">{user?.email}</p>
                <p className="text-xs capitalize mt-0.5 px-1.5 py-0.5 rounded-[4px] bg-info-blue/10 text-info-blue inline-block">{user?.role}</p>
                {typeof qCap === 'number' && (
                  <div className="mt-2" data-testid="quota-meter">
                    <div className="flex justify-between text-[10px] text-ink/50"><span>Queries today</span><span data-testid="quota-meter-text">{qCap === -1 ? `${qUsed} · unlimited` : `${Math.min(qUsed, qCap)} / ${qCap}`}</span></div>
                    {qCap !== -1 && <div className="h-1 rounded-full bg-ink/10 mt-1 overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, (qUsed / qCap) * 100)}%`, background: qUsed >= qCap ? '#DC2626' : qUsed / qCap > 0.8 ? '#B45309' : '#1A4D3A' }} /></div>}
                  </div>
                )}
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild data-testid="menu-account" className="cursor-pointer">
                <Link to="/account"><CreditCard className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />Account &amp; billing</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild data-testid="menu-pricing" className="cursor-pointer">
                <Link to="/pricing"><Sparkles className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />Plans &amp; pricing</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild data-testid="menu-verified" className="cursor-pointer">
                <Link to="/verified"><MapPin className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />Verified directory</Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="logout-btn" onClick={handleLogout} className="text-status-red cursor-pointer">
                <LogOut className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Mobile menu */}
          <Button variant="ghost" size="sm" className="lg:hidden" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {/* Mobile nav */}
      {mobileOpen && (
        <div className="lg:hidden mt-3 pt-3 border-t border-ink/10 flex flex-col gap-1" data-testid="mobile-nav-drawer">
          {allMobileItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setMobileOpen(false)}
              className={`flex items-center gap-2 px-3 py-2 rounded-[4px] font-body text-sm ${
                isActive(item.path) ? 'bg-brand-orange/10 text-brand-orange' : 'text-ink/60'
              }`}
            >
              <item.icon className="w-4 h-4" strokeWidth={1.5} />
              {item.label}
            </Link>
          ))}
          {hasClaims && (
            <Link to="/my-business" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-2 rounded-[4px] font-body text-sm text-ink/60" data-testid="nav-mobile-my-business">
              <Store className="w-4 h-4" strokeWidth={1.5} /> My Business
            </Link>
          )}
          {isAdmin && (
            <Link to="/admin" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-2 rounded-[4px] font-body text-sm text-ink/60">
              <Settings className="w-4 h-4" strokeWidth={1.5} /> Admin
            </Link>
          )}
        </div>
      )}
    </nav>
  );
}
